<template>
  <div class="regras-listagem">
    <Loading :isActive="loading && !jaCarregou" />

    <Edit :item="item" :close="closeEdit" :create="create" :update="update" :salvando="salvando" />

    <div class="regras-listagem__header">
      <CButton color="primary" @click="openCreate">
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Nova regra
      </CButton>
    </div>

    <div class="regras-listagem__filtros">
      <CRow>
        <CCol md="8" class="mb-2 mb-md-0">
          <div class="regras-listagem__busca">
            <input
              class="form-control"
              type="text"
              v-model="filtros.termo"
              placeholder="Buscar pela descrição..."
              aria-label="Buscar regra pela descrição"
              @input="loadRegrasComDebounce()"
              @keyup.enter="loadRegras()"
            />
            <fa-icon v-if="loading && jaCarregou" icon="spinner" spin class="regras-listagem__busca-indicador" />
          </div>
        </CCol>
        <CCol md="4">
          <input
            class="form-control"
            type="date"
            v-model="filtros.data"
            title="Vigentes na data"
            aria-label="Regras vigentes na data"
            @change="loadRegras()"
          />
        </CCol>
      </CRow>
      <div v-if="temFiltros" class="regra-negocio-listagem__filtros-ativos">
        <span>{{ descricaoFiltros }}</span>
        <button type="button" class="regra-negocio-listagem__limpar" @click="limparFiltros">Limpar filtros</button>
      </div>
    </div>

    <div :class="['regras-listagem__table-wrapper', 'regra-negocio-listagem__rolagem', { 'regras-listagem__table-wrapper--atualizando': loading && jaCarregou }]">
      <table class="regras-listagem__table">
        <thead>
          <tr>
            <th class="regras-listagem__col-id">ID</th>
            <th>Descrição</th>
            <th>Tipo</th>
            <th>Canais</th>
            <th class="regras-listagem__col-vigencia">Vigência</th>
            <th class="regra-negocio-listagem__col-situacao">Situação</th>
            <th class="regra-negocio-listagem__col-acoes"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="erroCarga">
            <td colspan="7" class="regras-listagem__empty">
              <div class="regras-listagem__erro">Não foi possível carregar as regras: {{ erroCarga }}</div>
              <CButton color="primary" variant="outline" size="sm" @click="loadRegras(paginaAtual)">Tentar novamente</CButton>
            </td>
          </tr>
          <tr v-else-if="jaCarregou && regras.length === 0">
            <td colspan="7" class="regras-listagem__empty">
              {{ temFiltros ? "Nenhuma regra encontrada para os filtros informados" : "Nenhuma regra de negócio cadastrada" }}
            </td>
          </tr>
          <tr v-for="regra in regras" :key="regra.id" class="regras-listagem__row">
            <td class="regras-listagem__col-id">{{ regra.id }}</td>
            <td><strong>{{ regra.descricao }}</strong></td>
            <td>
              <span class="regra-negocio-listagem__tipo">
                <fa-icon :icon="iconeTipo(regra.tipo)" class="regra-negocio-listagem__tipo-icone" />
                {{ rotuloTipo(regra.tipo) }}
              </span>
            </td>
            <td>
              <span v-for="canal in canais(regra)" :key="canal.campo" class="regras-badge regras-badge--canal">
                <img :src="canal.imagem" alt="" class="regras-badge__logo" />
                {{ canal.label }}
              </span>
              <span v-if="!canais(regra).length" class="regra-negocio-listagem__apoio">Nenhum canal</span>
            </td>
            <td class="regras-listagem__col-vigencia">{{ vigencia(regra) }}</td>
            <td class="regra-negocio-listagem__col-situacao">
              <span :class="['regras-badge', classeSituacao(regra)]">
                {{ situacao(regra).label }}
              </span>
            </td>
            <td class="regra-negocio-listagem__col-acoes">
              <CButton
                size="sm"
                color="primary"
                class="regras-listagem__botao-acao"
                title="Alterar"
                :aria-label="`Alterar a regra ${regra.descricao}`"
                @click="openEdit(regra)"
              >
                <fa-icon icon="pencil-alt" />
              </CButton>
              <CButton
                size="sm"
                color="danger"
                class="regras-listagem__botao-acao"
                title="Excluir"
                :aria-label="`Excluir a regra ${regra.descricao}`"
                @click="deleteRegra(regra)"
              >
                <fa-icon icon="trash-alt" />
              </CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="regras-listagem__pagination">
      <pagination :data="paginator" :limit="5" @pagination-change-page="loadRegras" />
    </div>
  </div>
</template>

<script>
import clone from "just-clone";
import _debounce from "lodash/debounce";
import { get, del, put, post } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";
import Edit from "./Edit.vue";
import { mensagemErroApi } from "@/views/Cashback/helpers";
import {
  DEBOUNCE_BUSCA_MS,
  ITENS_POR_PAGINA,
  MODAL_REGRA_NEGOCIO,
  canaisDaRegra,
  formatarData,
  novaRegraNegocio,
  rotuloTipoRegra,
  situacaoRegra,
  tipoRegra,
  vigenciaRegra,
} from "../constantes";

const CLASSES_SITUACAO = {
  VIGENTE: "regras-badge--ativo",
  AGENDADA: "regra-negocio-listagem__situacao--agendada",
  ENCERRADA: "regra-negocio-listagem__situacao--encerrada",
  INATIVA: "regras-badge--inativo",
};

export default {
  name: "listRegraNegocio",
  components: { Loading, Edit },

  data() {
    return {
      loading: false,
      salvando: false,
      jaCarregou: false,
      erroCarga: null,
      item: novaRegraNegocio(),
      regras: [],
      paginator: {},
      filtros: {
        data: "",
        termo: "",
      },
    };
  },

  computed: {
    paginaAtual() {
      return this.paginator.current_page || 1;
    },

    temFiltros() {
      return !!(this.filtros.termo || this.filtros.data);
    },

    descricaoFiltros() {
      return [
        this.filtros.termo ? `Descrição contendo "${this.filtros.termo}"` : null,
        this.filtros.data ? `Vigentes em ${formatarData(this.filtros.data)}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    },
  },

  methods: {
//        DESABILITADO POR MOTIVOS DE SEGURANÇA
        // case 'SP':
        //   return 'Script PHP'
    rotuloTipo: rotuloTipoRegra,

    iconeTipo(tipo) {
      return (tipoRegra(tipo) || { icone: "sliders-h" }).icone;
    },

    canais: canaisDaRegra,

    vigencia: vigenciaRegra,

    situacao: situacaoRegra,

    classeSituacao(regra) {
      return CLASSES_SITUACAO[situacaoRegra(regra).chave];
    },

    sucesso(json) {
      this.$swal({
        title: "Salvo com sucesso",
        text: json && json.message,
        showCancelButton: false,
        showConfirmButton: true,
        icon: "success",
      });
    },

    falha(err) {
      this.$swal({
        title: "Falha ao salvar",
        text: mensagemErroApi(err),
        icon: "error",
      });
    },

    salvar(requisicao) {
      this.salvando = true;

      return requisicao
        .then((json) => {
          this.closeEdit();
          this.loadRegras(this.paginaAtual);
          this.sucesso(json);
        })
        .catch(this.falha)
        .finally(() => {
          this.salvando = false;
        });
    },

    create(item) {
      return this.salvar(post("/admin/regras-negocio", item));
    },

    update(item) {
      return this.salvar(put(`/admin/regras-negocio/${item.id}`, item));
    },

    deleteRegra(regra) {
      this.$swal({
        title: "Excluir a regra de negócio?",
        text: `A regra "${regra.descricao}" deixa de ser validada nos pedidos. Essa ação não poderá ser desfeita.`,
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (!res.value) return;

          this.loading = true;
          del(`/admin/regras-negocio/${regra.id}`)
            .then(() => {
              this.$swal({ title: "Removida com sucesso!", icon: "success" });

              return this.loadRegras(this.regras.length === 1 && this.paginaAtual > 1 ? this.paginaAtual - 1 : this.paginaAtual);
            })
            .catch((err) => {
              this.$swal({ title: "Falha ao remover", text: mensagemErroApi(err), icon: "error" });
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(() => {});
    },

    openCreate() {
      this.item = novaRegraNegocio();
      this.$modal.show(MODAL_REGRA_NEGOCIO);
    },

    openEdit(regra) {
      this.item = clone(regra);
      this.$modal.show(MODAL_REGRA_NEGOCIO);
    },

    closeEdit() {
      this.$modal.hide(MODAL_REGRA_NEGOCIO);
    },

    loadRegras(page = 1) {
      this.loading = true;
      this.erroCarga = null;
      const sequencia = (this.sequenciaCarga = (this.sequenciaCarga || 0) + 1);
      const params = new URLSearchParams({
        take: ITENS_POR_PAGINA,
        page,
        termo: this.filtros.termo,
        data: this.filtros.data || "",
      });

      return get(`/admin/regras-negocio?${params}`)
        .then((json) => {
          if (sequencia !== this.sequenciaCarga) return;

          this.regras = json.itens || [];
          this.paginator = json.paginator || {};
        })
        .catch((err) => {
          if (sequencia !== this.sequenciaCarga) return;

          this.regras = [];
          this.erroCarga = mensagemErroApi(err);
        })
        .finally(() => {
          if (sequencia !== this.sequenciaCarga) return;

          this.loading = false;
          this.jaCarregou = true;
        });
    },

    limparFiltros() {
      this.filtros.termo = "";
      this.filtros.data = "";
      this.loadRegras();
    },
  },

  created() {
    this.loadRegrasComDebounce = _debounce(() => this.loadRegras(), DEBOUNCE_BUSCA_MS);
  },

  beforeMount() {
    this.loadRegras();
  },

  beforeDestroy() {
    this.loadRegrasComDebounce.cancel();
  },
};
</script>

<style scoped src="@/components/regras/regras-listagem.css"></style>

<style scoped>
.regra-negocio-listagem__rolagem {
  overflow-x: auto;
}

.regra-negocio-listagem__rolagem .regras-listagem__table {
  min-width: 860px;
}

.regra-negocio-listagem__filtros-ativos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 12px;
  color: #667085;
}

.regra-negocio-listagem__limpar {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: var(--cashback-cor-primaria);
  text-decoration: underline;
  cursor: pointer;
}

.regra-negocio-listagem__tipo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.regra-negocio-listagem__tipo-icone {
  color: var(--cashback-cor-primaria);
}

.regra-negocio-listagem__apoio {
  font-size: 11.5px;
  color: #98a2b3;
}

.regra-negocio-listagem__situacao--agendada {
  background: #eff8ff;
  color: #175cd3;
  border-color: #d1e9ff;
}

.regra-negocio-listagem__situacao--encerrada {
  background: #fffaeb;
  color: #b54708;
  border-color: #fedf89;
}

.regra-negocio-listagem__col-situacao {
  width: 100px;
  text-align: center;
}

.regra-negocio-listagem__col-acoes {
  width: 100px;
  text-align: center;
  white-space: nowrap;
}
</style>
